/**
 * A program's Savanna-backed courses, with content for the first one only.
 *
 * Two requests, because the systems are keyed differently: eHub answers which
 * curriculum and batch back each of the program's courses
 * (`savannaCoursesForProgram`, read from `ClassLMSLocation.lmsCourseId` and
 * `lmsCohortId`), and Savanna answers what is inside a curriculum. A field
 * value cannot be fed into a sibling field's argument in one document, so the
 * ids have to come back first.
 *
 * The root query is used rather than `Program.savannaCourses`, because that
 * field is only reachable through `viewer.availablePrograms`, which takes no
 * arguments: asking there would make every program in the catalogue resolve its
 * class and LMS-location join to answer about one program.
 *
 * Only the FIRST course's content is fetched. The program detail page expands
 * course 1 and renders the rest as closed, inert headers, so a curriculum for
 * courses 2..N would be paid for and never shown. The trade is visible on those
 * rows: with no flow there is no lock, no free/paid split and no activity
 * count, so they carry a number and a name and nothing else.
 *
 * The ids are answered per caller: once enrolled, `savannaCoursesForProgram`
 * reports the class the learner is actually in rather than the one a newcomer
 * would join, so the curriculum follows the learner.
 *
 * The first-curriculum read is not wasted on the enrolled path, where the
 * interactive tree ignores `course` and fetches per course instead: that
 * tree opens course 1 on arrival and asks for it with the same id and
 * `sandId`, so Apollo answers the second ask from the cache. The two must
 * keep sending identical variables for that to hold.
 */
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import {
  GetSavannaCoursesForProgramDocument,
  GetSavannaCurriculumDocument,
} from '@sandtech-hub-monorepo/ehub-client';

import { Course } from './programs';
import {
  SavannaCourseLocation,
  savannaCourseToCourse,
} from './savannaCurriculum';

export interface UseProgramCurriculumResult {
  /** Every Savanna-backed course in the program, in course order. */
  locations: SavannaCourseLocation[];
  /** The first course with its Savanna content, or undefined if there is none. */
  course: Course | undefined;
  loading: boolean;
  /**
   * True when either read failed. Callers must distinguish this from an empty
   * `locations`: without it, a failed request and a program with no Savanna
   * content look identical, and the page says the program has no curriculum
   * when in fact nothing was asked successfully.
   */
  failed: boolean;
}

export const useProgramCurriculum = (
  programId: string,
  sandId: string | null
): UseProgramCurriculumResult => {
  const {
    data: coursesData,
    loading: coursesLoading,
    error: coursesError,
  } = useQuery(GetSavannaCoursesForProgramDocument, {
    variables: { programId },
    fetchPolicy: 'cache-first',
  });

  const locations = useMemo(
    () => coursesData?.savannaCoursesForProgram ?? [],
    [coursesData]
  );
  const first = locations[0];

  /*
   * `sandId` is omitted by leaving it OUT of this object rather than setting it
   * to null: an absent nullable variable makes the argument absent, while an
   * explicit null is a value Savanna would have to interpret. Hence the
   * conditional spread.
   *
   * With it, Savanna returns this learner's flow and `isPassed` means
   * something. Without it, the read is the informative one - the curriculum
   * described on its own terms, for someone deciding whether to join.
   *
   * No `batchId`, by design. Asking by curriculum alone is what makes the
   * unenrolled read describe the course rather than one class's released copy
   * of it, and on the enrolled read Savanna ignores the argument anyway.
   */
  const variables = useMemo(
    () => ({
      id: first?.curriculumId ?? '',
      ...(sandId ? { sandId } : {}),
    }),
    [first, sandId]
  );

  const { data, loading, error } = useQuery(GetSavannaCurriculumDocument, {
    variables,
    skip: !first,
    fetchPolicy: 'cache-first',
  });

  const course = useMemo(
    () =>
      first
        ? savannaCourseToCourse(
            first,
            1,
            data?.savannaCurriculum?.flow,
            !!sandId
          )
        : undefined,
    [first, data, sandId]
  );

  return {
    locations,
    course,
    loading: coursesLoading || loading,
    failed: !!coursesError || !!error,
  };
};
